import forge from 'node-forge';
import sentinelNetwork from '@/lib/api/sentinelNetwork';

/**
 * Encrypt a string using AES-256-CBC + RSA-OAEP hybrid encryption.
 * Matches the cloud-app/docufy encryption format.
 *
 * @returns `encryptedAESKey:encryptedData:iv` (base64, colon-separated)
 */
export const encryptString = (str: string, publicKeyPem: string): string => {
  const aesKey = forge.random.getBytesSync(32);

  const cipher = forge.cipher.createCipher('AES-CBC', aesKey);
  const iv = forge.random.getBytesSync(16);
  cipher.start({ iv });
  cipher.update(forge.util.createBuffer(str));
  cipher.finish();
  const encryptedData = cipher.output;

  const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
  const encryptedAESKey = publicKey.encrypt(aesKey, 'RSA-OAEP', {
    md: forge.md.sha256.create(),
  });

  const encryptedAESKeyBase64 = forge.util.encode64(encryptedAESKey);
  const encryptedDataBase64 = forge.util.encode64(encryptedData.getBytes());
  const ivBase64 = forge.util.encode64(iv);

  return `${encryptedAESKeyBase64}:${encryptedDataBase64}:${ivBase64}`;
};

/**
 * Fetch the RSA public key from Sentinel's /entitlements endpoint.
 */
export const fetchPublicKey = async (): Promise<string> => {
  const entitlements = await fetchEntitlements();
  return entitlements.publicKey;
};

export const fetchEntitlements = async (): Promise<{ publicKey: string; userEmail: string; userRoles: string[] }> => {
  const res = await sentinelNetwork.get('/entitlements');

  const data = res.data;

  const publicKey = data?.public_key || '';
  const userEmail = data?.user_email || '';
  const userRoles: string[] = data?.roles || [];

  if (!publicKey) {
    throw new Error('Public key not available from Sentinel');
  }

  return { publicKey, userEmail, userRoles };
};
